import axios, { AxiosResponse } from 'axios';
import { Storage } from 'react-jhipster';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { serializeAxiosError } from './reducer.utils';

import { AppDispatch, AppThunk } from 'app/config/store';
import { getFirebaseToken } from 'app/firebase';
import { toast } from 'react-hot-toast';
import Cookies from 'js-cookie';
import { postingRulesType } from 'app/utils/types/activity/activity-types';
import { Navigate, useNavigate } from 'react-router-dom';
import { getUserPreferencesData, sendUserPreferencesData } from 'app/shared/reducers/user-preferences';
import { clearExistingActivities } from './activity-filter';
import { jwtDecode } from 'jwt-decode';
import { refreshToken } from 'app/config/axios-interceptor';

export const AUTH_TOKEN_KEY = 'jhi-authenticationToken';
const USER_PREFERENCES = 'userPreferencesCompleted';
const myCookieValue = Cookies.get('jhi-authenticationToken');
const token = myCookieValue ? myCookieValue : Storage.local.get('jhi-authenticationToken');

// Persist consent state across page refreshes
const storedConsentStatus = localStorage.getItem('consentStatus') || null;
const storedConsentPending = storedConsentStatus === 'PENDING' || storedConsentStatus === 'DECLINED' || storedConsentStatus === 'EXPIRED';
const storedMaskedParentEmail = localStorage.getItem('maskedParentEmail') || null;
const storedCanResend = localStorage.getItem('canResend') === 'true';
const storedNextResendAt = localStorage.getItem('nextResendAt') ? Number(localStorage.getItem('nextResendAt')) : null;

export const initialState = {
  loading: false,
  isAuthenticated: token ? true : false,
  loginSuccess: false,
  loginError: false, // Errors returned from server side
  showModalLogin: false,
  account: {} as any,
  errorMessage: null as unknown as string, // Errors returned from server side
  redirectMessage: null as unknown as string,
  sessionHasBeenFetched: false,
  logoutUrl: null as unknown as string,
  userPreferencesDone: false,
  banned: false,
  responseObj: {} as any,
  consentPending: storedConsentPending,
  consentStatus: storedConsentStatus as string | null,
  maskedParentEmail: storedMaskedParentEmail as unknown as string,
  canResend: storedCanResend,
  nextResendAt: storedNextResendAt as number | null,
  pendingSocialSignup: null as null | {
    pendingSignupToken: string;
    email: string;
    firstName: string;
    lastName: string;
    imageUrl?: string;
    provider: string;
  },
};

export type AuthenticationState = Readonly<typeof initialState>;

// Actions
export const toggleModalLogin = (): AppThunk => dispatch => {
  dispatch(AuthenticationSlice.actions.toggleModalLogin());
};

export const fetchConsentStatus = createAsyncThunk('authentication/fetch_consent_status', async (_, { rejectWithValue }) => {
  try {
    return await axios.get<any>('api/parental-consent/status');
  } catch {
    return rejectWithValue(null);
  }
});

export const getSession = (): AppThunk => (dispatch, getState) => {
  dispatch(getAccount());
  dispatch(getUserPreferencesData());
  dispatch(fetchConsentStatus());
  const token =
    Cookies.get('jhi-authenticationToken') ||
    Storage.local.get('jhi-authenticationToken') ||
    Storage.session.get('jhi-authenticationToken');
  if (token) {
    scheduleTokenRefresh(token, dispatch as AppDispatch);
  }
};

export const getAccount = createAsyncThunk('authentication/get_account', async (_, { rejectWithValue }) => {
  try {
    return await axios.get<any>('api/account');
  } catch (error: any) {
    return rejectWithValue({ status: error?.response?.status, message: error?.message });
  }
});

interface registerParams {
  deviceId: string;
  fcmTokenId: string;
}
interface IAuthParams {
  email: string;
  password: string;
  rememberMe?: boolean;
}
interface GSocialLoginParams {
  code: string;
}

let refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;

let dispatchRef: AppDispatch | null = null;

const scheduleTokenRefresh = (jwtToken: string, dispatch?: AppDispatch) => {
  if (dispatch) dispatchRef = dispatch;
  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }
  try {
    const decoded: any = jwtDecode(jwtToken);
    const expiresAt = decoded.exp * 1000;
    const refreshAt = expiresAt - 5 * 60 * 1000;
    const delay = refreshAt - Date.now();
    if (delay > 0) {
      refreshTimeoutId = setTimeout(async () => {
        const newToken = await refreshToken();
        refreshTimeoutId = null;
        if (!newToken && dispatchRef) {
          dispatchRef(logout());
        }
      }, delay);
    }
  } catch {
    // ignore decode errors — token will expire naturally
  }
};

// Status returned by POST api/account/socialLogin for a brand-new (or previously-deleted)
// Google/Apple account — no account exists yet, the frontend must collect DOB + phone
// before calling completeSocialSignup.
const PROFILE_INCOMPLETE = 'PROFILE_INCOMPLETE';

// Shared by login/GoogleSocialLogin/AppleSocialLogin/completeSocialSignup — persists the
// tokens and side effects of a successful authentication, regardless of which sign-up/sign-in
// path produced it.
const persistAuthSuccess = (data: any, dispatch: AppDispatch) => {
  fetchPostingRules();
  localStorage.setItem('openOnce', JSON.stringify('once'));
  localStorage.setItem('Banned', data?.banned);
  const userPreferecesDone = data?.isUserPreferenceDone;
  Storage.session.set(USER_PREFERENCES, userPreferecesDone === true);
  const bearerToken = data?.jwtToken?.id_token;
  localStorage.setItem('refreshToken', data?.refreshToken);
  localStorage.setItem('tokenExpiryTime', data?.tokenExpiryTime);
  localStorage.setItem('consentPending', data?.consentPending ? 'true' : 'false');
  localStorage.setItem('maskedParentEmail', data?.maskedParentEmail || '');
  Cookies.set('jhi-authenticationToken', bearerToken);
  Storage.local.set(AUTH_TOKEN_KEY, bearerToken);
  Storage.session.set(AUTH_TOKEN_KEY, bearerToken);
  scheduleTokenRefresh(bearerToken, dispatch);
  if (bearerToken) {
    dispatch(clearExistingActivities());
    handleGetFirebaseToken();
  }
};

export const GoogleSocialLogin = createAsyncThunk(
  'authentication/social_login',
  async (params: GSocialLoginParams, { rejectWithValue, dispatch }) => {
    try {
      const result = await axios.post<any>('api/account/socialLogin', { ...params, provider: 'GOOGLE', platform: 'web' });
      if (result?.data?.status === PROFILE_INCOMPLETE) {
        return result.data;
      }
      if (result?.request?.status === 200 || result?.request?.status === 201) {
        persistAuthSuccess(result.data, dispatch as AppDispatch);
      }
      return result.data;
    } catch (error) {
      return rejectWithValue(serializeAxiosError(error));
    }
  }
);

export const AppleSocialLogin = createAsyncThunk('authentication/socialLogin', async (params: any, { rejectWithValue, dispatch }) => {
  try {
    const { code, idToken, email, firstName, lastName } = params;
    const result = await axios.post<any>('api/account/socialLogin', {
      code,
      name: `${firstName || ''} ${lastName || ''}`.trim(),
      provider: 'APPLE',
      platform: 'web',
    });
    if (result?.data?.status === PROFILE_INCOMPLETE) {
      return result.data;
    }
    if (result?.request?.status === 200 || result?.request?.status === 201) {
      persistAuthSuccess(result.data, dispatch as AppDispatch);
    }
    return result.data;
  } catch (error) {
    return rejectWithValue(serializeAxiosError(error));
  }
});

interface SocialSignupCompletionParams {
  pendingSignupToken: string;
  dateOfBirth: string;
  phoneNumber: string;
  countryCode: string;
  isoCode: string;
  parentEmail?: string;
}

// Finishes a brand-new Google/Apple sign-up once DOB + an already-verified phone number
// (via /api/phone/verify-pre-registration) are collected. Routes through the same age rule
// and consent logic as email/password signup server-side.
export const completeSocialSignup = createAsyncThunk(
  'authentication/complete_social_signup',
  async (params: SocialSignupCompletionParams, { rejectWithValue, dispatch }) => {
    try {
      const result = await axios.post<any>('api/account/socialLogin/complete', params);
      persistAuthSuccess(result.data, dispatch as AppDispatch);
      return result.data;
    } catch (error) {
      return rejectWithValue(serializeAxiosError(error));
    }
  }
);

export const authenticate = createAsyncThunk(
  'authentication/login',
  async (auth: IAuthParams) => axios.post<any>('api/authenticate', auth),
  {
    serializeError: serializeAxiosError,
  }
);

const registerDevice = async () => {
  try {
    const randomDeviceId = `device-${Math.floor(Math.random() * 10000)}-${Date.now()}`;
    localStorage.setItem('randomDeviceId', randomDeviceId);
    let params = { deviceId: randomDeviceId, fcmTokenId: JSON.parse(localStorage.getItem('firebaseToken')) };
    const response = await axios.post<any>('api/users/register-device', params);
  } catch (error) {
    console.log('Error fetching register device:', error);
  }
};

export const unregisterDevice = createAsyncThunk(
  'register-device-firebase',
  async (params: registerParams, { rejectWithValue }) => {
    try {
      const response = await axios.post<any>('api/users/logout', params);
      localStorage.clear();
      sessionStorage.clear();
      toast.success(response.data.message);
      return response;
    } catch (error) {
      return rejectWithValue(serializeAxiosError(error));
    }
  },
  {
    serializeError: serializeAxiosError,
  }
);
export const login: (email: string, password: string, rememberMe?: boolean) => AppThunk =
  (email, password, rememberMe = false) =>
  async dispatch => {
    const result = await dispatch(authenticate({ email, password, rememberMe }));
    const response = result.payload as AxiosResponse;
    if (response?.status === 200 || response?.status === 201) {
      persistAuthSuccess(response.data, dispatch as AppDispatch);
    }
  };

const fetchPostingRules = async () => {
  try {
    const response = await axios.get<postingRulesType[]>('api/volunteer_instructions');
    Storage.local.set('postingRules', response.data);
  } catch (error) {
    console.log(error);
  }
};

export const getPhoneNumber = createAsyncThunk<any, void, { rejectValue: string }>(
  'phone/getPhoneNumber',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<any>('api/userPhone');
      if (response?.data) {
        Storage.local.set('getPhone', response.data);
      }
      return response?.data;
    } catch (error) {
      console.log(error, 'error');
      return rejectWithValue(error.response?.data?.message || 'An error occurred');
    }
  }
);
const handleGetFirebaseToken = () => {
  getFirebaseToken()
    .then(firebaseToken => {
      Storage.local.set('firebaseToken', firebaseToken);
      if (firebaseToken) {
        registerDevice();
      }
    })
    .catch(err => console.error('An error occured while retrieving firebase token. ', err));
};

export const clearAuthToken = () => {
  sessionStorage.clear();
  const keyToPreserve = 'dark-mode';
  for (const key in localStorage) {
    if (localStorage.hasOwnProperty(key) && key !== keyToPreserve) {
      localStorage.removeItem(key);
    }
  }
};

export const logout: () => AppThunk = () => async dispatch => {
  dispatch(logoutSession());
  localStorage.removeItem('consentStatus');
  localStorage.removeItem('consentPending');
  localStorage.removeItem('maskedParentEmail');
  localStorage.removeItem('canResend');
  localStorage.removeItem('nextResendAt');
  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }
  Cookies.remove('jhi-authenticationToken');

  const allCookies = Cookies.get();
  Object.keys(allCookies).forEach(cookieName => {
    Cookies.remove(cookieName);
  });
  clearAuthToken();

  await clearBrowserCache();
};
async function clearBrowserCache() {
  if ('caches' in window) {
    const cacheKeys = await caches.keys();
    for (const cacheName of cacheKeys) {
      await caches.delete(cacheName);
    }
    console.log('All browser caches have been cleared.');
  }
}
export const clearAuthentication = messageKey => dispatch => {
  clearAuthToken();
  dispatch(authError(messageKey));
  dispatch(clearAuth());
};

export const AuthenticationSlice = createSlice({
  name: 'authentication',
  initialState: initialState as AuthenticationState,
  reducers: {
    toggleModalLogin: state => {
      return {
        ...state,
        showModalLogin: true,
      };
    },
    setAuthenticationFalse() {
      return {
        ...initialState,
        isAuthenticated: false,
      };
    },
    logoutSession() {
      return {
        ...initialState,
        showModalLogin: true,
        isAuthenticated: false,
        consentPending: false,
        consentStatus: null,
        maskedParentEmail: null,
      };
    },
    authError(state, action) {
      return {
        ...state,
        showModalLogin: true,
        isAuthenticated: false,
        redirectMessage: action.payload,
      };
    },
    clearAuth(state) {
      return {
        ...state,
        loading: false,
        showModalLogin: true,
        isAuthenticated: false,
      };
    },
    resetLogin(state) {
      return {
        ...state,
        loginError: false,
        errorMessage: null,
      };
    },
    updateMaskedParentEmail(state, action) {
      state.maskedParentEmail = action.payload;
    },
    clearConsentPending(state) {
      state.consentPending = false;
      state.maskedParentEmail = null;
    },
    clearPendingSocialSignup(state) {
      state.pendingSocialSignup = null;
    },
    setAuthentication(state) {
      const localToken = Storage.local.get(AUTH_TOKEN_KEY);
      const sessionToken = Storage.session.get(AUTH_TOKEN_KEY);
      const myCookieValue = Cookies.get('jhi-authenticationToken');
      const authenticationToken = myCookieValue;
      const isCookie = !!authenticationToken;

      state.isAuthenticated = !!localToken || !!sessionToken || isCookie;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(authenticate.rejected, (state, action) => {
        let errorMessage = action.error.message;

        // Check if it's a network error
        if (action.error.message && action.error.message.includes('Network Error')) {
          errorMessage = 'Unable to connect. Please check your internet connection and try again.';
        }
        return {
          ...initialState,
          errorMessage: errorMessage,
          showModalLogin: true,
          isAuthenticated: false,
          loginError: true,
          userPreferencesDone: false,
          loginSuccess: false,
        };
      })
      .addCase(authenticate.fulfilled, (state, action) => {
        return {
          ...state,
          loading: false,
          loginError: false,
          showModalLogin: false,
          loginSuccess: true,
          isAuthenticated: true,
          userPreferencesDone: action.payload.data.isUserPreferenceDone,
          banned: action.payload.data.banned,
          responseObj: action.payload.data,
          consentPending: action.payload.data.consentPending ?? false,
          maskedParentEmail: action.payload.data.maskedParentEmail ?? null,
        };
      })
      .addCase(getAccount.rejected, (state, action) => {
        const payload = action.payload as { status?: number; message?: string } | undefined;
        const status = payload?.status;
        if (status === 401) {
          return {
            ...state,
            loading: false,
            isAuthenticated: false,
            sessionHasBeenFetched: true,
            showModalLogin: true,
            errorMessage: payload?.message,
          };
        }
        return {
          ...state,
          loading: false,
          sessionHasBeenFetched: true,
        };
      })
      .addCase(getAccount.fulfilled, (state, action) => {
        const isAuthenticated = action.payload && action.payload.data && action.payload.data.activated;
        localStorage.setItem('MyDetailsAccount', JSON.stringify(action.payload.data));
        return {
          ...state,
          isAuthenticated,
          loading: false,
          sessionHasBeenFetched: true,
          account: action.payload.data,
        };
      })
      .addCase(fetchConsentStatus.fulfilled, (state, action) => {
        const data = action.payload?.data;
        const status = data?.status ?? null;
        const showBanner = status === 'PENDING' || status === 'DECLINED' || status === 'EXPIRED';
        const masked = data?.maskedParentEmail ?? null;
        const canResend = data?.canResend ?? false;
        const nextResendAt = data?.nextResendAt ?? null;
        localStorage.setItem('consentStatus', status ?? '');
        localStorage.setItem('consentPending', showBanner ? 'true' : 'false');
        if (masked) localStorage.setItem('maskedParentEmail', masked);
        localStorage.setItem('canResend', canResend ? 'true' : 'false');
        if (nextResendAt) localStorage.setItem('nextResendAt', String(nextResendAt));
        else localStorage.removeItem('nextResendAt');
        return { ...state, consentStatus: status, consentPending: showBanner, maskedParentEmail: masked, canResend, nextResendAt };
      })
      .addCase(authenticate.pending, state => ({
        ...initialState,
        loading: true,
        showModalLogin: true,
        isAuthenticated: false,
      }))
      .addCase(GoogleSocialLogin.fulfilled, (state, action) => {
        const data = action.payload;
        if (data?.status === PROFILE_INCOMPLETE) {
          return { ...state, loading: false, pendingSocialSignup: data };
        }
        return {
          ...state,
          loading: false,
          loginError: false,
          showModalLogin: false,
          loginSuccess: true,
          isAuthenticated: true,
          userPreferencesDone: true,
          consentPending: data?.consentPending ?? false,
          maskedParentEmail: data?.maskedParentEmail ?? null,
          pendingSocialSignup: null,
        };
      })
      .addCase(GoogleSocialLogin.rejected, (state, action) => ({
        ...initialState,
        isAuthenticated: false,
        showModalLogin: true,
        errorMessage: action.error.message,
        loginError: true,
        loginSuccess: false,
        userPreferencesDone: false,
      }))
      .addCase(AppleSocialLogin.fulfilled, (state, action) => {
        const data = action.payload;
        if (data?.status === PROFILE_INCOMPLETE) {
          return { ...state, loading: false, pendingSocialSignup: data };
        }
        return {
          ...state,
          loading: false,
          loginError: false,
          showModalLogin: false,
          loginSuccess: true,
          isAuthenticated: true,
          userPreferencesDone: true,
          consentPending: data?.consentPending ?? false,
          maskedParentEmail: data?.maskedParentEmail ?? null,
          pendingSocialSignup: null,
        };
      })
      .addCase(AppleSocialLogin.rejected, (state, action) => ({
        ...initialState,
        isAuthenticated: false,
        showModalLogin: true,
        errorMessage: action.error.message,
        loginError: true,
        loginSuccess: false,
        userPreferencesDone: false,
      }))
      .addCase(completeSocialSignup.fulfilled, (state, action) => {
        const data = action.payload;
        return {
          ...state,
          loading: false,
          loginError: false,
          showModalLogin: false,
          loginSuccess: true,
          isAuthenticated: true,
          userPreferencesDone: true,
          consentPending: data?.consentPending ?? false,
          maskedParentEmail: data?.maskedParentEmail ?? null,
          pendingSocialSignup: null,
        };
      })
      .addCase(completeSocialSignup.rejected, (state, action) => ({
        ...state,
        loading: false,
        errorMessage: (action.payload as any)?.message || action.error.message,
        loginError: true,
      }));
  },
});

export const {
  logoutSession,
  authError,
  clearAuth,
  resetLogin,
  setAuthentication,
  setAuthenticationFalse,
  clearPendingSocialSignup,
} = AuthenticationSlice.actions;

// Reducer
export default AuthenticationSlice.reducer;
